import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { word } from "@/modules/core/utils/i18n"
import type { UserProgramDetailsEntity } from "@/modules/programs/entities/userProgramDetailsEntity"
import { ProgramType } from "@/modules/programs/types/programTypes"
import { ProgramOnOffSessionEntity } from "@/modules/sessions/entities/programOnOffSessionEntity"
import { useProgramSessionActions } from "@/modules/sessions/hooks/useProgramSessionActions"
import {
  getProgramOnOffSessionValidationSchema,
  type ProgramOnOffSessionFormikType,
} from "@/modules/sessions/programSessionFormikType"
import { useCreateSessionTemplate } from "@/modules/template/hooks/useTemplate"
import { useFormik } from "formik"
import { useCallback, useState } from "react"

export const useProgramOnOffSessionFormik = ({
  program,
  editedSession,
  dateSession,
}: {
  program: UserProgramDetailsEntity
  editedSession?: ProgramOnOffSessionEntity
  dateSession?: string | number
}) => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const { createSession, updateSession } = useProgramSessionActions()
  const { mutateAsync: createSessionTemplate } = useCreateSessionTemplate()
  const getInitialValues = useCallback(() => {
    if (editedSession) {
      return ProgramOnOffSessionEntity.fromEntityToFormik(editedSession)
    } else {
      return ProgramOnOffSessionEntity.getDraftFormik(program.id, dateSession)
    }
  }, [editedSession, program.id, dateSession])

  const sessionFormSubmitHandler = async (sessionFormikValues: ProgramOnOffSessionFormikType) => {
    if (editedSession) {
      return await updateSession({
        id: editedSession.id,
        programId: editedSession.programId,
        programType: ProgramType.ONOFF,
        session: ProgramOnOffSessionEntity.fromFormikToInputDto(sessionFormikValues),
      })
    } else {
      return await createSession({
        programId: program.id,
        programType: ProgramType.ONOFF,
        session: ProgramOnOffSessionEntity.fromFormikToInputDto(sessionFormikValues),
      })
    }
  }

  const formik = useFormik<ProgramOnOffSessionFormikType>({
    initialValues: getInitialValues(),
    validationSchema: getProgramOnOffSessionValidationSchema,
    onSubmit: async () => {
      setIsSubmitting(true)

      try {
        const result = await sessionFormSubmitHandler(formik.values)
        if (formik.values.isTemplateSaved && result) {
          await createSessionTemplate({
            name: formik.values.name,
            description: null,
            templateCategoryId: null,
            sessionId: result.id,
          })
        }
        setIsSubmitting(false)
        toast.success(editedSession ? word("program.session.update.success") : word("program.session.create.success"), {
          classNames: successToastClassName(),
        })
        return true
      } catch (e) {
        setIsSubmitting(false)
        console.error("Session form submit error", e)
        toast.error(word("global.error.default"), {
          classNames: errorToastClassName(),
        })
        return false
      }
    },
  })

  return { formik, isSubmitting }
}
